import React, { useState } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Switch,
  FormControlLabel,
  Snackbar,
  Alert,
} from '@mui/material';
import { Add, Edit, Delete, Star, StarBorder } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getApiUrl } from '../config/api';

interface Currency {
  id: number;
  code: string;
  name: string;
  symbol: string;
  exchange_rate: number | string; // API might return as string
  is_default: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface CurrencyFormData {
  code: string;
  name: string;
  symbol: string;
  exchange_rate: number;
  is_active: boolean;
}

// API functions
const currencyApi = {
  async getAll(): Promise<Currency[]> {
    const response = await fetch(getApiUrl('/currencies'), {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch currencies');
    }
    
    const data = await response.json();
    return Array.isArray(data.data) ? data.data : [];
  },

  async create(data: CurrencyFormData): Promise<Currency> {
    const response = await fetch(getApiUrl('/currencies'), {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to create currency');
    }
    
    const result = await response.json();
    return result.data;
  },

  async update(id: number, data: CurrencyFormData): Promise<Currency> {
    const response = await fetch(getApiUrl(`/currencies/${id}`), {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to update currency');
    }
    
    const result = await response.json();
    return result.data;
  },

  async delete(id: number): Promise<void> {
    const response = await fetch(getApiUrl(`/currencies/${id}`), {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to delete currency');
    }
  },

  async setDefault(id: number): Promise<Currency> {
    const response = await fetch(getApiUrl(`/currencies/${id}/set-default`), {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to set default currency');
    }
    
    const result = await response.json();
    return result.data;
  },
};

const CurrenciesPage: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCurrency, setEditingCurrency] = useState<Currency | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const { control, handleSubmit, formState: { errors }, reset } = useForm<CurrencyFormData>({
    defaultValues: {
      code: '',
      name: '',
      symbol: '',
      exchange_rate: 1,
      is_active: true,
    },
  });

  // Fetch currencies from API
  const { data: currencies = [], isLoading } = useQuery({
    queryKey: ['currencies'],
    queryFn: currencyApi.getAll,
  });

  const createCurrencyMutation = useMutation({
    mutationFn: currencyApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['currencies'] });
      setSnackbar({ open: true, message: t('currency_created'), severity: 'success' });
      setOpenDialog(false);
      reset();
    },
    onError: (error: Error) => {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    },
  });

  const updateCurrencyMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: CurrencyFormData }) => currencyApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['currencies'] });
      setSnackbar({ open: true, message: t('currency_updated'), severity: 'success' });
      setOpenDialog(false);
      setEditingCurrency(null);
      reset();
    },
    onError: (error: Error) => {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    },
  });

  const deleteCurrencyMutation = useMutation({
    mutationFn: currencyApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['currencies'] });
      setSnackbar({ open: true, message: t('currency_deleted'), severity: 'success' });
    },
    onError: (error: Error) => {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    },
  });

  const setDefaultCurrencyMutation = useMutation({
    mutationFn: currencyApi.setDefault,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['currencies'] });
      setSnackbar({ open: true, message: t('default_currency_updated'), severity: 'success' });
    },
    onError: (error: Error) => {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    },
  });

  const handleCreate = () => {
    setEditingCurrency(null);
    reset({
      code: '',
      name: '',
      symbol: '',
      exchange_rate: 1,
      is_active: true,
    });
    setOpenDialog(true);
  };

  const handleEdit = (currency: Currency) => {
    setEditingCurrency(currency);
    reset({
      code: currency.code,
      name: currency.name,
      symbol: currency.symbol,
      exchange_rate: Number(currency.exchange_rate),
      is_active: currency.is_active,
    });
    setOpenDialog(true);
  };

  const handleDelete = (id: number) => {
    if (window.confirm(t('confirm_delete_currency'))) {
      deleteCurrencyMutation.mutate(id);
    }
  };

  const handleSetDefault = (id: number) => {
    setDefaultCurrencyMutation.mutate(id);
  };

  const onSubmit = (data: CurrencyFormData) => {
    if (editingCurrency) {
      updateCurrencyMutation.mutate({ id: editingCurrency.id, data });
    } else {
      createCurrencyMutation.mutate(data);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCurrency(null);
    reset();
  };

  const defaultCurrency = Array.isArray(currencies) ? currencies.find(c => c.is_default) : undefined;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          {t('currencies')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreate}
        >
          {t('add_currency')}
        </Button>
      </Box>

      {/* Loading State */}
      {isLoading && (
        <Box display="flex" justifyContent="center" py={4}>
          <Typography>{t('loading')}</Typography>
        </Box>
      )}

      {/* Default Currency Info */}
      {!isLoading && defaultCurrency && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('default_currency')}
            </Typography>
            <Box display="flex" alignItems="center" gap={2}>
              <Chip
                icon={<Star />}
                label={`${defaultCurrency.code} - ${defaultCurrency.name}`}
                color="primary"
                variant="filled"
              />
              <Typography variant="body2" color="textSecondary">
                {t('base_exchange_rate')}: 1.00
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Currencies Table */}
      {!isLoading && (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('currency_code')}</TableCell>
                  <TableCell>{t('currency_name')}</TableCell>
                  <TableCell>{t('symbol')}</TableCell>
                  <TableCell>{t('exchange_rate')}</TableCell>
                  <TableCell>{t('status')}</TableCell>
                  <TableCell>{t('default')}</TableCell>
                  <TableCell>{t('actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.isArray(currencies) && currencies.map((currency) => (
                  <TableRow key={currency.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {currency.code}
                      </Typography>
                    </TableCell>
                    <TableCell>{currency.name}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {currency.symbol}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {Number(currency.exchange_rate).toFixed(4)}
                      </Typography>
                      {defaultCurrency && currency.id !== defaultCurrency.id && (
                        <Typography variant="caption" color="textSecondary" display="block">
                          1 {defaultCurrency.code} = {Number(currency.exchange_rate).toFixed(4)} {currency.code}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={currency.is_active ? t('active') : t('inactive')}
                        color={currency.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleSetDefault(currency.id)}
                        disabled={currency.is_default || setDefaultCurrencyMutation.isPending}
                        color={currency.is_default ? 'primary' : 'default'}
                      >
                        {currency.is_default ? <Star /> : <StarBorder />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(currency)}
                        color="primary"
                        disabled={updateCurrencyMutation.isPending}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(currency.id)}
                        color="error"
                        disabled={currency.is_default || deleteCurrencyMutation.isPending}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Add/Edit Currency Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCurrency ? t('edit_currency') : t('add_currency')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <Controller
                name="code"
                control={control}
                rules={{ 
                  required: t('currency_code_required'),
                  pattern: {
                    value: /^[A-Z]{3}$/,
                    message: t('currency_code_format')
                  }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('currency_code')}
                    fullWidth
                    error={!!errors.code}
                    helperText={errors.code?.message || t('currency_code_help')}
                    inputProps={{ maxLength: 3, style: { textTransform: 'uppercase' } }}
                    onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="symbol"
                control={control}
                rules={{ required: t('currency_symbol_required') }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('symbol')}
                    fullWidth
                    error={!!errors.symbol}
                    helperText={errors.symbol?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="name"
                control={control}
                rules={{ required: t('currency_name_required') }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('currency_name')}
                    fullWidth
                    error={!!errors.name}
                    helperText={errors.name?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="exchange_rate"
                control={control}
                rules={{ 
                  required: t('exchange_rate_required'),
                  min: {
                    value: 0.0001,
                    message: t('exchange_rate_min')
                  }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('exchange_rate')}
                    type="number"
                    fullWidth
                    error={!!errors.exchange_rate}
                    helperText={errors.exchange_rate?.message || t('exchange_rate_help')}
                    inputProps={{ step: 0.0001, min: 0.0001 }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="is_active"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value}
                        onChange={field.onChange}
                        color="primary"
                      />
                    }
                    label={t('active')}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            {t('cancel')}
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            disabled={createCurrencyMutation.isPending || updateCurrencyMutation.isPending}
          >
            {editingCurrency ? t('update') : t('create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CurrenciesPage;
